import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { formatIsoDate } from '../../../lib/format';
import styles from './RateStepChart.module.css';

// Exchange rates, Option A (Admin redesign 2026-11-02): one pair's rates as a staircase, because each rate holds until
// the next. Our own SVG, drawn in the box's real pixels so its labels stay 12px at every width (critique: 9px axis
// text). The positions are screen geometry worked out in floating point; the rates themselves stay exact text.

/** The box's height, as `--nr-fx-chart-h`. */
export const CHART_HEIGHT = 190;
const FALLBACK_WIDTH = 640;
const PAD = { top: 14, right: 16, bottom: 30, left: 56 } as const;
const TICKS = 4;
const POINT_R = 4;
const DAY_MS = 86_400_000;
const MAX_MONTH_LABELS = 7;

export interface RatePoint {
  readonly effectiveFrom: string;
  readonly rate: string;
}

export interface StepChartGeometry {
  readonly path: string;
  readonly points: readonly { readonly x: number; readonly y: number; readonly label: string }[];
  readonly yTicks: readonly { readonly y: number; readonly label: string }[];
  readonly xTicks: readonly { readonly x: number; readonly label: string }[];
}

function dayNumber(isoDay: string): number {
  return Date.parse(`${isoDay}T00:00:00Z`) / DAY_MS;
}

function isoOfDay(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

const MONTH = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' });
const MONTH_YEAR = new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });

/**
 * Where everything goes. `rates` oldest first. `from` is the first day shown (null: from the first rate); the line runs
 * to `today` or the last rate, whichever is later. A rate before `from` is carried in from the left edge.
 */
export function stepChartGeometry(rates: readonly RatePoint[], from: string | null, today: string, width: number): StepChartGeometry | null {
  const first = rates[0];
  const last = rates[rates.length - 1];
  if (!first || !last) return null;
  const start = from === null ? dayNumber(first.effectiveFrom) : Math.max(dayNumber(from), dayNumber(first.effectiveFrom));
  const end = Math.max(dayNumber(today), dayNumber(last.effectiveFrom), start + 1);
  const carried = [...rates].reverse().find((rate) => dayNumber(rate.effectiveFrom) <= start);
  const shown = rates.filter((rate) => dayNumber(rate.effectiveFrom) > start);
  const steps = [...(carried ? [{ day: start, value: Number(carried.rate), label: carried.rate, isCarried: dayNumber(carried.effectiveFrom) < start }] : []), ...shown.map((rate) => ({ day: dayNumber(rate.effectiveFrom), value: Number(rate.rate), label: rate.rate, isCarried: false }))];
  const values = steps.map((step) => step.value);
  const low = Math.min(...values);
  const high = Math.max(...values);
  const spread = high - low || Math.abs(high) * 0.02 || 1;
  const bottom = low - spread * 0.15;
  const top = high + spread * 0.15;
  const plotW = Math.max(width - PAD.left - PAD.right, 1);
  const plotH = CHART_HEIGHT - PAD.top - PAD.bottom;
  const xOf = (day: number): number => PAD.left + ((day - start) / (end - start)) * plotW;
  const yOf = (value: number): number => PAD.top + (1 - (value - bottom) / (top - bottom)) * plotH;

  const path = steps.map((step, index) => (index === 0 ? `M${xOf(step.day)},${yOf(step.value)}` : `H${xOf(step.day)} V${yOf(step.value)}`)).join(' ') + ` H${xOf(end)}`;
  const decimals = Math.min(8, Math.max(0, Math.ceil(-Math.log10((top - bottom) / TICKS)) + 1));
  const yTicks = Array.from({ length: TICKS + 1 }, (_, index) => {
    const value = bottom + ((top - bottom) * index) / TICKS;
    return { y: yOf(value), label: value.toFixed(decimals) };
  });

  const months: { day: number; date: Date }[] = [];
  const cursor = new Date(start * DAY_MS);
  cursor.setUTCDate(1);
  cursor.setUTCMonth(cursor.getUTCMonth() + (cursor.getTime() / DAY_MS < start ? 1 : 0));
  while (cursor.getTime() / DAY_MS <= end) {
    months.push({ day: cursor.getTime() / DAY_MS, date: new Date(cursor) });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  const every = Math.max(1, Math.ceil(months.length / MAX_MONTH_LABELS));
  const xTicks = months
    .filter((_, index) => index % every === 0)
    .map(({ day, date }, index) => ({ x: xOf(day), label: index === 0 || date.getUTCMonth() === 0 ? MONTH_YEAR.format(date) : MONTH.format(date) }));

  return { path, points: steps.filter((step) => !step.isCarried).map((step) => ({ x: xOf(step.day), y: yOf(step.value), label: `${step.label} from ${formatIsoDate(isoOfDay(step.day))}` })), yTicks, xTicks };
}

interface RateStepChartProps {
  /** Oldest first. */
  readonly rates: readonly RatePoint[];
  readonly from: string | null;
  readonly today: string;
  /** "1 USD in BDT, last 6 months". */
  readonly title: string;
}

export function RateStepChart({ rates, from, today, title }: RateStepChartProps): ReactNode {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);
  const titleId = useId();
  const descId = useId();

  useLayoutEffect(() => {
    const element = box.current;
    if (!element) return undefined;
    const measure = (): void => setWidth(element.clientWidth || FALLBACK_WIDTH);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const geometry = stepChartGeometry(rates, from, today, width);
  if (!geometry) return null;
  const words = rates.map((rate) => `${rate.rate} from ${formatIsoDate(rate.effectiveFrom)}`).join('; ');
  return (
    <div ref={box} className={styles.box}>
      <svg className={styles.chart} width={width} height={CHART_HEIGHT} viewBox={`0 0 ${width} ${CHART_HEIGHT}`} role="img" aria-labelledby={`${titleId} ${descId}`}>
        <title id={titleId}>{title}</title>
        <desc id={descId}>{words}</desc>
        <g aria-hidden>
          {geometry.yTicks.map((tick, index) => (
            <g key={index}>
              <line className={styles.grid} x1={PAD.left} x2={width - PAD.right} y1={tick.y} y2={tick.y} />
              <text className={styles.label} x={PAD.left - 8} y={tick.y} textAnchor="end" dominantBaseline="middle">
                {tick.label}
              </text>
            </g>
          ))}
          {geometry.xTicks.map((tick, index) => (
            <text key={index} className={styles.label} x={tick.x} y={CHART_HEIGHT - 8} textAnchor="middle">
              {tick.label}
            </text>
          ))}
          <path className={styles.line} d={geometry.path} />
          {geometry.points.map((point, index) => (
            <circle key={index} className={styles.point} cx={point.x} cy={point.y} r={POINT_R}>
              <title>{point.label}</title>
            </circle>
          ))}
        </g>
      </svg>
    </div>
  );
}
